import { addObject } from './objects.js';
import { putBlob } from '../services/media-store.js';
import { upload } from '../services/assets.js';
import { canAddVideo, canUpload } from '../services/limits.js';
import { isSignedIn } from '../services/auth.js';
import { renderRails } from '../ui/rails.js';
import { requestRender } from '../state/bus.js';
import { $ } from '../state/store.js';
import { toast } from '../ui/toast.js';
import { MAX_VIDEOS, lib, primeBlob, videoReaderCount } from './video.js';

/* BRINGING A CLIP IN.

   PROBE, THEN CHECK, THEN STORE — the same order services/fonts.js's
   addFontFile uses, and for the same reason: a file that turns out not to be
   a decodable video must leave nothing behind. No blob in IndexedDB, no row
   in the assets table, no object in the scene. Doing it the other way round
   (add the object, then discover the file is a .mov full of ProRes the
   browser cannot decode) leaves a broken plane someone has to notice and
   delete by hand.

   The probe is also the only way to get the three numbers the timing model
   needs — duration, width, height — and it gets them from the same demuxer
   that will later decode the frames, so the metadata cannot disagree with
   what actually plays.

   Bytes never enter the document. They go into IndexedDB as a Blob and, when
   there is an account to put them under, straight up to storage as well. See
   services/media-store.js for why a data URL is not an option here. */

/**
 * Read a video file's metadata without decoding any of it.
 * @returns {Promise<{seconds:number, width:number, height:number, codec:string}>}
 */
async function probe(file) {
  const { Input, BlobSource, MP4, QTFF, MATROSKA, WEBM, CanvasSink, canDecodeVideo } =
    await lib();
  const input = new Input({ formats: [MP4, QTFF, MATROSKA, WEBM], source: new BlobSource(file) });
  const track = await input.getPrimaryVideoTrack();
  if (!track) throw new Error('That file has no video track in it.');

  const codec = await track.getCodec();
  const width = await track.getDisplayWidth();
  const height = await track.getDisplayHeight();
  const seconds = await input.computeDuration();
  /* The container's own packet rate, sampled rather than assumed — it is the
     only way to tell a 24fps clip from a 30fps one, and the difference
     decides whether it will land 1:1 in this project or stutter. Best
     effort: a variable-rate clip has no single answer and older builds of
     the demuxer may not offer the call at all, so a failure here leaves the
     readout silent rather than blocking an import that would otherwise
     work. */
  let fps = 0;
  try {
    const stats = await track.computePacketStats?.(200);
    fps = Math.round((stats?.averagePacketRate || 0) * 1000) / 1000;
  } catch (_) {}

  /* Asked before anything is stored rather than discovered on the first
     frame. A container this browser can parse is not the same as a codec it
     can decode — an H.265 MP4 parses perfectly and then renders nothing. */
  if (!(await canDecodeVideo(codec, { width, height })))
    throw new Error(`This browser cannot decode ${codec} at ${width} × ${height}. Re-export the clip as H.264.`);

  /* DOES IT ACTUALLY HAVE ALPHA? Asked by decoding one frame and looking,
     not by trusting the container. A WebM can declare an alpha channel and
     carry nothing in it, and an opaque clip decoded as transparent costs a
     blend and a sorting problem for no picture — so this reads the corners of
     a real frame and only says yes when something is genuinely see-through.

     Corners rather than the whole frame: a matte is transparent at its edges
     essentially by definition, and sampling four pixels is free where reading
     a 4K frame back is not. */
  let alpha = false;
  try {
    const sink = new CanvasSink(track, { width: 64, height: 64, fit: 'fill', alpha: true });
    const it = sink.canvasesAtTimestamps([Math.min(0.1, seconds / 2)]);
    const frame = (await it.next()).value;
    if (frame) {
      const cv = document.createElement('canvas');
      cv.width = cv.height = 64;
      const cx = cv.getContext('2d', { alpha: true });
      cx.clearRect(0, 0, 64, 64);
      cx.drawImage(frame.canvas, 0, 0);
      const d = cx.getImageData(0, 0, 64, 64).data;
      const at = (x, y) => d[(y * 64 + x) * 4 + 3];
      alpha = Math.min(at(0,0), at(63,0), at(0,63), at(63,63)) < 250;
    }
  } catch (_) { /* unreadable here is not fatal — it plays opaque */ }

  return { seconds, width, height, codec, fps, alpha };
}

/**
 * Add a video object from a picked file. Throws with a sentence worth showing
 * if the file cannot be used; the caller turns that into a toast.
 */
/**
 * Everything a clip needs before it can belong to an object: probe it, check
 * it against the limits, store the bytes, and get them uploaded if there is
 * anywhere to upload to.
 *
 * Split out of importVideo so that REPLACING the file on an existing layer
 * runs the identical pipeline — same probe, same limits, same storage, same
 * upload — instead of a second, slightly different one that would drift.
 *
 * @returns {Promise<{blobId:string, assetId:string|null, meta:object}>}
 */
export async function prepareVideo(file) {
  const meta = await probe(file);

  const allowed = canAddVideo({ bytes: file.size, seconds: meta.seconds, width: meta.width, height: meta.height });
  if (!allowed.ok) throw new Error(allowed.reason);

  /* Local first, always — so a clip works offline, works signed out, and does
     not need a round trip before it appears on screen. */
  const blobId = await putBlob(file, { mime: file.type, seconds: meta.seconds, width: meta.width, height: meta.height });
  /* The decoder is about to want exactly these bytes, and we are holding
     them. Handing them over directly means the first frame never waits on
     storage — and never races the write we just made. */
  primeBlob(blobId, file);

  /* And to the cloud too when there is somewhere to put it, rather than
     waiting for the next save: the upload is the slow part, and doing it here
     means it overlaps with the person positioning the plane instead of
     stalling the save they make afterwards. A refusal is not fatal — the clip
     still works locally, and toV2 will try again on save. */
  let assetId = null;
  if (isSignedIn()) {
    try {
      const ok = await canUpload(file.size);
      if (ok.ok) assetId = (await upload(file, 'video')).id;
    } catch (err) {
      console.warn('[illusio] clip upload deferred', err);
    }
  }

  return { blobId, assetId, meta };
}

/**
 * Add a video object from a picked file. Throws with a sentence worth showing
 * if the file cannot be used; the caller turns that into a toast.
 */
export async function importVideo(file) {
  /* Only when ADDING. A replace hands its object's decoder back before it
     opens the new one, so it never needs a slot it did not already hold. */
  if (videoReaderCount() >= MAX_VIDEOS)
    throw new Error(`${MAX_VIDEOS} clips is the limit in one scene — each one holds a hardware video decoder open.`);

  const { blobId, assetId, meta } = await prepareVideo(file);

  const o = addObject('video', {
    blobId, assetId,
    srcDur: meta.seconds, srcW: meta.width, srcH: meta.height, srcFps: meta.fps,
    srcAlpha: meta.alpha
  });
  o.name = file.name.replace(/\.[^.]+$/, '');
  renderRails();
  requestRender();
  return o;
}

/* Wired here rather than in scene/objects.js, where the image and GLB pickers
   live at the bottom of the file. Those two are there for historical reasons
   and are the odd ones out; a new type does not need to join them. */
const input = $('#fileVideo');
if (input) input.onchange = async e => {
  const f = e.target.files[0];
  e.target.value = '';                    // so the same file can be picked twice
  if (!f) return;
  try { await importVideo(f); }
  catch (err) { toast(err.message || 'That clip could not be added.', true); }
};
